import { useNavigate } from 'react-router';
import { useEffect, useState } from 'react';
import {
  useBankIDAccountConfirmMutation,
  useBankIDSessionsQuery,
  useBankIDSessionsRejectMutation,
} from 'hooks/webapi';
import { MANUAL_PSD2_BANKS } from 'Router/routes';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import SelectEvent from 'hooks/context/EventCollector/events/SelectEvent';
import useNavigateByIdentificationStatus from 'hooks/useNavigateByIdentificationStatus';
import { handleErrorRedirect } from 'pages/Registration/BankId/BankInfo/useBankIdInfo';

export default function useBankIdAccountSelection() {
  // TODO: Handle case where localStorage bankIdSessionId is not set
  const sessionId = localStorage.getItem('bankIdSessionId');
  const navigate = useNavigate();
  const { track } = useEventCollector();
  const [selectedAccount, setSelectedAccount] = useState('');
  const { navigateByIdentificationStatus } =
    useNavigateByIdentificationStatus('bankId');

  const {
    data: bankIdSessionData,
    isLoading: bankIdSessionIsLoading,
    isFetched: isFetchedBankIdSessionData,
  } = useBankIDSessionsQuery({ uuid: sessionId! }, Boolean(sessionId));
  const { mutateAsync: rejectBankID, isPending: rejecdBankIdIsPending } =
    useBankIDSessionsRejectMutation();
  const {
    mutateAsync: confirmBankIDAccount,
    isPending: confirmBankIdIsPending,
  } = useBankIDAccountConfirmMutation();

  const [accountList, setAccountList] = useState<string[]>([]);

  // Collapses a multi-account list to the first entry; expands back to the full account list
  const toggleAccounts = () => {
    if (accountList.length > 1) {
      setAccountList([accountList[0]]);
      setSelectedAccount(accountList[0]);
    } else if (bankIdSessionData?.paymentAccounts) {
      setAccountList(bankIdSessionData.paymentAccounts);
    }
  };

  const handleSelectAccount = (account: string) => {
    setSelectedAccount(account);
  };

  const confirmBankIdAccountNumber = async () => {
    // If sessionId is not loaded, we can't proceed
    if (!sessionId) {
      handleErrorRedirect(navigate, 'session_id_not_loaded');
      return;
    }

    if (!selectedAccount) {
      return;
    }

    await confirmBankIDAccount({
      uuid: sessionId,
      bankAccountNumber: selectedAccount,
    });
    navigateByIdentificationStatus();
  };

  const rejectBankIdAccountNumber = async () => {
    // If sessionId is not loaded, we can't proceed
    if (!sessionId) {
      handleErrorRedirect(navigate, 'session_id_not_loaded');
      return;
    }

    await rejectBankID(sessionId);

    navigate(MANUAL_PSD2_BANKS);
  };

  // Only one account available
  useEffect(() => {
    if (
      bankIdSessionData?.paymentAccounts &&
      bankIdSessionData.paymentAccounts.length === 1
    ) {
      setSelectedAccount(bankIdSessionData.paymentAccounts[0]);
    }
  }, [bankIdSessionData?.paymentAccounts]);

  // Track selected account
  useEffect(() => {
    if (selectedAccount === '') {
      return;
    }

    track(
      new SelectEvent({
        value: selectedAccount,
        description: 'selectedAccount',
      }),
    );
  }, [selectedAccount, track]);

  useEffect(() => {
    if (bankIdSessionData?.paymentAccounts) {
      setAccountList(bankIdSessionData?.paymentAccounts);
    }
  }, [bankIdSessionData?.paymentAccounts]);

  return {
    isPending: rejecdBankIdIsPending || confirmBankIdIsPending,
    isLoading: bankIdSessionIsLoading,
    isFetchedAccountList: isFetchedBankIdSessionData,
    accountList,
    selectedAccount,
    handleSelectAccount,
    confirmBankIdAccountNumber,
    rejectBankIdAccountNumber,
    toggleAccounts,
  };
}
